import { forwardRef, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Container,
  Typography,
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepButton,
  Button,
} from "@mui/material";
import LiveBidProducts from "./LiveBidProducts";
import styles from "./LivebidDetail.module.scss";
import { useLocation } from "react-router-dom";
import _sendAPIRequest from "../../../helpers/api";
import { PortalApiUrls } from "../../../helpers/api-urls/PortalApiUrls";
import { dateTimeFormatter, truncateString } from "../../../helpers/formatter";
import ScreenLoader from "../../../elements/CustomScreeenLoader/ScreenLoader";
import classNames from "classnames";
import {
  Analytics,
  Campaign,
  CheckCircle,
  Groups,
  HourglassTop,
  Lock,
} from "@mui/icons-material";

function LivebidDetail() {
  const type = new URLSearchParams(useLocation().search).get("type");
  const bidId = new URLSearchParams(useLocation().search).get("id");
  const location = useLocation();
  const [bidProducts, setBidProduct] = useState([]);
  const [screenLoader, setScreenLoader] = useState(true);
  const [timeUpFlag, setTimeUpFlag] = useState(false);
  const { bid, listType } = location.state || {};
  const [isBidClosed, setBidClosed] = useState(
    bid?.bid_close_date ? new Date(bid.bid_close_date) < new Date() : false,
  );
  const [isAgreed, setIsAgreed] = useState(
    JSON.parse(localStorage.getItem("isAgreed")) || false,
  );
  const hoursRef = useRef();
  const minutesRef = useRef();
  const secondsRef = useRef();

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const closeTime = new Date(bid?.bid_close_date);
      const difference = closeTime - now;

      if (difference > 0) {
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / (1000 * 60)) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        if (hoursRef.current)
          hoursRef.current.textContent = String(hours).padStart(2, "0");
        if (minutesRef.current)
          minutesRef.current.textContent = String(minutes).padStart(2, "0");
        if (secondsRef.current)
          secondsRef.current.textContent = String(seconds).padStart(2, "0");
      } else {
        if (hoursRef.current) hoursRef.current.textContent = "00";
        if (minutesRef.current) minutesRef.current.textContent = "00";
        if (secondsRef.current) hoursRef.current.textContent = "00";

        setTimeUpFlag(true);
        setBidClosed(true);
      }
    };

    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [bid?.bid_close_date]);

  const fetchLiveBidData = async () => {
    try {
      const url =
        type === "created"
          ? PortalApiUrls.LIVE_BID_PRODUCTS_BUYER
          : PortalApiUrls.LIVE_BID_PRODUCTS_SUPPLIER;
      const response = await _sendAPIRequest(
        "GET",
        `${url}${bidId}/`,
        "",
        true,
      );
      if (response.status === 200) {
        setBidProduct(response.data);
        const hasParticipant = response.data.some(
          (product) => product.participant !== null,
        );
        if (hasParticipant) {
          setIsAgreed(true);
          localStorage.setItem("isAgreed", JSON.stringify(true));
        }
        setScreenLoader(false);
      }
    } catch (error) {
      console.error("Error fetching live bid data:", error);
    }
  };

  const retrieveLiveBid = async () => {
    try {
      const response = await _sendAPIRequest(
        "GET",
        listType === "Created"
          ? `${PortalApiUrls.LIVE_BID_LIST_FOR_BUYER}?${bidId}`
          : PortalApiUrls.LIVE_BID_LIST_FOR_SUPPLIER,
        "",
        true,
      );

      if (response.status === 200) {
        if (response?.data?.live_bids.length === 0) {
          setBidClosed(true);
        }
      }
    } catch (error) {
      console.error("Error fetching live bids:", error);
    }
  };

  const refetchLiveBidState = () => {
    fetchLiveBidData();
    retrieveLiveBid();
  };

  useEffect(() => {
    if (isBidClosed) return;
    refetchLiveBidState();
    const interval = setInterval(refetchLiveBidState, 10000);
    return () => clearInterval(interval);
  }, [isBidClosed]);

  const handleDataUpdate = () => {
    fetchLiveBidData();
  };

  if (screenLoader) {
    return <ScreenLoader />;
  }

  let steps = [];

  if (type === "created") {
    steps = [
      {
        label: "Bid Closed",
        icon: <Lock />,
        link: `/portal/bids/details/${bidId}`,
        description:
          "The live bidding window has ended. No further bids or modifications can be made.",
      },
      {
        label: "Bid Participation",
        icon: <Groups />,
        link: `/portal/bids/details/${bidId}?activeTab=${bid?.type === "QCBS" ? "5" : "4"}`,
        description:
          "Review how many suppliers participated and submitted their quotations for this bid.",
      },
      {
        label: "Evaluate & Award",
        icon: <Analytics />,
        link: `/portal/bids/details/${bidId}?activeTab=${bid?.type === "QCBS" ? "6" : "5"}`,
        description:
          "Analyze supplier quotations, compare pricing and terms, then finalize the supplier selection.",
      },
      {
        label: "Bid Completed",
        icon: <CheckCircle />,
        link: `/portal/bids/details/${bidId}`,
        description:
          "The bidding process is successfully completed and the supplier has been finalized.",
      },
    ];
  } else {
    steps = [
      {
        label: "Bid Closed",
        icon: <Lock />,
        link: `/portal/bids/details/${bidId}/?type=invited`,
        description:
          "The live bidding window has ended. No further bids or revisions can be submitted.",
      },
      {
        label: "Await Results",
        icon: <HourglassTop />,
        link: `/portal/bids/details/${bidId}/?type=invited`,
        description:
          "Your quotation has been successfully recorded. Please wait while the buyer reviews submissions and declares the results.",
      },
      {
        label: "Results Declared",
        icon: <Campaign />,
        link: `/portal/bids/details/${bidId}/?type=invited`,
        description:
          "The buyer has completed the evaluation process. You can now check the bid outcome and status.",
      },
      {
        label: "Bid Completed",
        icon: <CheckCircle />,
        link: `/portal/bids/details/${bidId}/?type=invited`,
        description:
          "The bidding process is fully completed and the final supplier selection has been made.",
      },
    ];
  }

  return (
    <Container maxWidth="lg">
      <Grid
        container
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2
        }}>
        <Typography className={styles.bidTitle}>
          {truncateString(bid?.title, 50)}
        </Typography>
        {!isBidClosed && (
          <Box className={styles.timerWrapper}>
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "1px",
                textTransform: "uppercase",
                color: "#062d72",
                mb: 1,
              }}
            >
              Bid ends in
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: 0.9,
              }}
            >
              <TimeUnit label="HRS" ref={hoursRef} />
              <Colon />
              <TimeUnit label="MIN" ref={minutesRef} />
              <Colon />
              <TimeUnit label="SEC" ref={secondsRef} highlight />
            </Box>
          </Box>
        )}
      </Grid>
      {bid !== undefined && (
        <Typography className={styles.bidMeta} sx={{ mt: 6 }}>
          <span>
            Bid ID: <strong>{bid?.formatted_number}</strong>
          </span>

          <span className={styles.metaSeparator} />

          {type !== "created" && (
            <>
              <span>
                Buyer: <strong>{bid?.company?.name}</strong>
              </span>
              <span className={styles.metaSeparator} />
            </>
          )}

          <span>
            Published: <strong>{dateTimeFormatter(bid?.created_at)}</strong>
          </span>
        </Typography>
      )}
      {!isBidClosed &&
        bidProducts.map((liveBidProduct, index) => (
          <LiveBidProducts
            key={index}
            liveBidproduct={liveBidProduct}
            type={type}
            onUpdate={handleDataUpdate}
            timeUpFlag={timeUpFlag}
            bidCloseTime={bid?.bid_close_date}
            screenLoader={screenLoader}
          />
        ))}
      {isBidClosed && (
        <>
          <Alert
            severity="info"
            sx={{ mt: 5, mb: 5 }}
            action={
              type === "created" && (
                <Button
                  className={styles.proceed_further}
                  size="medium"
                  href={`/portal/bids/details/${bidId}?activeTab=${bid?.type === "QCBS" ? "5" : "4"}`}
                >
                  Proceed Further
                </Button>
              )
            }
          >
            <AlertTitle>Bid Closed</AlertTitle>
            The bidding window has closed.{" "}
            {type === "created"
              ? "You can now review supplier quotations and proceed with the selection process."
              : "Please wait while the buyer reviews submissions and you will notified when bid result is declared"}
          </Alert>

          <Stepper orientation="vertical" nonLinear>
            {steps.map((step) => (
              <Step key={step.label} active completed>
                <StepLabel
                  slots={{
                    stepIcon: () => (
                      <Box className={styles.stepIcon}>{step.icon}</Box>
                    )
                  }}
                >
                  <Typography
                    component="a"
                    href={step.link}
                    className={styles.stepLink}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>

                <StepContent>
                  <Typography>{step.description}</Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </>
      )}
    </Container>
  );
}

export default LivebidDetail;

const TimeUnit = forwardRef(({ label, highlight }, ref) => (
  <Box sx={{ textAlign: "center" }}>
    <Box
      className={classNames(
        styles.timeBox,
        highlight && styles.timeBoxHighlight,
      )}
    >
      <span ref={ref}>00</span>
    </Box>

    <Typography
      sx={{
        fontSize: "0.55rem",
        fontWeight: 600,
        letterSpacing: "1px",
        color: "text.secondary",
        mt: 0.4,
      }}
    >
      {label}
    </Typography>
  </Box>
));

const Colon = () => (
  <Typography
    sx={{
      fontSize: "1rem",
      fontWeight: 700,
      color: "rgba(25,118,210,0.55)",
      mt: 0.6,
    }}
  >
    :
  </Typography>
);
